import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const CertificationsPage = lazy(() => import('../pages/certifications/page'));
const TrainingPage = lazy(() => import('../pages/training/page'));
const CoursesPage = lazy(() => import('../pages/courses/page'));
const PrivacyPolicyPage = lazy(() => import('../pages/privacy-policy/page'));
const TermsOfServicePage = lazy(() => import('../pages/terms-of-service/page'));
const AntiBriberyPolicyPage = lazy(() => import('../pages/anti-bribery-policy/page'));
const SafeguardingPolicyPage = lazy(() => import('../pages/safeguarding-policy/page'));
const HumanRightsPolicyPage = lazy(() => import('../pages/human-rights-policy/page'));
const EqualityDiversityInclusionPolicy = lazy(() => import('../pages/equality-diversity-inclusion-policy/page'));
const EnvironmentalSustainabilityPolicy = lazy(() => import('../pages/environmental-sustainability-policy/page'));
const MartynsLawPage = lazy(() => import('../pages/martyns-law/page'));

// Service Pages
const SecuritySystemsPage = lazy(() => import('../pages/services/security-systems/page'));
const FireSafetyPage = lazy(() => import('../pages/services/fire-safety/page'));
const RiskAssessmentPage = lazy(() => import('../pages/services/risk-assessment/page'));
const CompliancePage = lazy(() => import('../pages/services/compliance/page'));
const MaintenancePage = lazy(() => import('../pages/services/maintenance/page'));
const ConsultancyPage = lazy(() => import('../pages/services/consultancy/page'));
const SecurityEventOperationsPage = lazy(() => import('../pages/services/security-event-operations/page'));

const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Wrapper component to handle lazy loading with Suspense
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div>Loading...</div>}>
    {children}
  </Suspense>
);

// Helper function to wrap lazy components
const lazyElement = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <LazyWrapper>
    <Component />
  </LazyWrapper>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: lazyElement(HomePage)
  },
  {
    path: '/about',
    element: lazyElement(AboutPage)
  },
  {
    path: '/certifications',
    element: lazyElement(CertificationsPage)
  },
  {
    path: '/training',
    element: lazyElement(TrainingPage)
  },
  {
    path: '/courses',
    element: lazyElement(CoursesPage)
  },
  {
    path: '/privacy-policy',
    element: lazyElement(PrivacyPolicyPage)
  },
  {
    path: '/terms-of-service',
    element: lazyElement(TermsOfServicePage)
  },
  {
    path: '/anti-bribery-policy',
    element: lazyElement(AntiBriberyPolicyPage)
  },
  {
    path: '/safeguarding-policy',
    element: lazyElement(SafeguardingPolicyPage)
  },
  {
    path: '/human-rights-policy',
    element: lazyElement(HumanRightsPolicyPage)
  },
  {
    path: '/equality-diversity-inclusion-policy',
    element: lazyElement(EqualityDiversityInclusionPolicy)
  },
  {
    path: '/environmental-sustainability-policy',
    element: lazyElement(EnvironmentalSustainabilityPolicy)
  },
  {
    path: '/health-safety-policy',
    lazy: async () => {
      const { default: Component } = await import('../pages/health-safety-policy/page');
      return { Component };
    },
  },
  {
    path: '/martyns-law',
    element: lazyElement(MartynsLawPage)
  },
  {
    path: '/services/security-systems',
    element: lazyElement(SecuritySystemsPage)
  },
  {
    path: '/services/fire-safety',
    element: lazyElement(FireSafetyPage)
  },
  {
    path: '/services/risk-assessment',
    element: lazyElement(RiskAssessmentPage)
  },
  {
    path: '/services/compliance',
    element: lazyElement(CompliancePage)
  },
  {
    path: '/services/maintenance',
    element: lazyElement(MaintenancePage)
  },
  {
    path: '/services/consultancy',
    element: lazyElement(ConsultancyPage)
  },
  {
    path: '/services/security-event-operations',
    element: lazyElement(SecurityEventOperationsPage)
  },
  {
    path: '*',
    element: lazyElement(NotFoundPage)
  }
];

export default routes;
